import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import InboxLink from '@/components/InboxLink';
import OrganizationBrand from '@/components/OrganizationBrand';
import {
  ArrowLeft,
  CalendarClock,
  ChevronDown,
  Gavel,
  Landmark,
  ListTodo,
  LogOut,
  Menu,
  Settings,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NAVIGATION = [
  { name: 'Overzicht', href: '/leiderschap', icon: Landmark },
  { name: 'Vergaderingen', href: '/leiderschap/vergaderingen', icon: CalendarClock },
  { name: 'Besluiten', href: '/leiderschap/besluiten', icon: Gavel },
  { name: 'Actiepunten', href: '/leiderschap/actiepunten', icon: ListTodo },
  { name: 'Instellingen', href: '/leiderschap/instellingen', icon: Settings, requiresManage: true },
];

/**
 * Module-eigen sidebar voor Leiderschap, zelfde patroon als
 * components/WorshipLayout.jsx en components/SprekersLayout.jsx: één ingang
 * in de globale sidebar ("Leiderschap" -> /leiderschap), interne navigatie
 * via deze eigen, volledige sidebar in plaats van losse items in de
 * globale navigatie.
 */
const LeiderschapLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasCapability } = useAuth();
  const canManage = hasCapability('leiderschap_manage');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigation = NAVIGATION.filter((item) => !item.requiresManage || canManage);

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Navigatie sluiten"
          className="fixed inset-0 z-40 bg-slate-950/45 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="border-b border-slate-100 px-5 py-5">
          <div className="flex items-center justify-between">
            <Link to="/leiderschap" className="flex items-center gap-3">
              <OrganizationBrand />
            </Link>
            <button type="button" className="text-slate-500 lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-600 text-white">
              <Landmark className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-400">Module</p>
              <p className="font-semibold text-slate-900">Leiderschap</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {navigation.map((item) => {
            const active = item.href === '/leiderschap'
              ? location.pathname === item.href
              : location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors ${active ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-100 p-3">
          {!user?.restricted_to_worship && (
            <Link to="/dashboard" className="mb-2 flex min-h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" />
              Terug naar Zoea
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-slate-50">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-100 font-medium text-teal-800">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {canManage && (
                <DropdownMenuItem onClick={() => navigate('/leiderschap/instellingen')}>
                  <Settings className="mr-2 h-4 w-4" /> Instellingen
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-slate-200 bg-white/95 px-4 backdrop-blur sm:px-6 lg:px-8">
          <button type="button" aria-label="Navigatie openen" className="text-slate-600 lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <InboxLink title="Inbox" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default LeiderschapLayout;
